import { supabase } from "@/integrations/supabase/client";

export const VAPID_PUBLIC_KEY =
  "BCJKczqc3H8BRNGwBFMtSfJ3lnP54fZY9MKYockTz-9qVF7XwUzJ92GO6A2X3mw4NyN0dDd2XflDmhjUe7xlwWg";

export function isPushSupported(): boolean {
  return (
    typeof window !== "undefined" && "serviceWorker" in navigator && "PushManager" in window && "Notification" in window
  );
}

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const output = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) output[i] = raw.charCodeAt(i);
  return output;
}

function bufToB64(buf: ArrayBuffer | null): string {
  if (!buf) return "";
  const bytes = new Uint8Array(buf);
  let bin = "";
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
  return btoa(bin).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

async function getRegistration(): Promise<ServiceWorkerRegistration> {
  const existing = await navigator.serviceWorker.getRegistration("/push-sw.js");
  if (existing) return existing;
  return navigator.serviceWorker.register("/push-sw.js", { scope: "/" });
}

export async function enablePush(userId: string): Promise<void> {
  if (!isPushSupported()) throw new Error("Push not supported in this browser");
  const permission = await Notification.requestPermission();
  if (permission !== "granted") throw new Error("Permission denied");

  const reg = await getRegistration();
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    const key = urlBase64ToUint8Array(VAPID_PUBLIC_KEY);
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: key.buffer.slice(key.byteOffset, key.byteOffset + key.byteLength) as ArrayBuffer,
    });
  }
  const json = sub.toJSON();
  const endpoint = json.endpoint || sub.endpoint;
  const p256dh = (json.keys && json.keys.p256dh) || bufToB64(sub.getKey?.("p256dh") ?? null);
  const auth = (json.keys && json.keys.auth) || bufToB64(sub.getKey?.("auth") ?? null);
  if (!endpoint || !p256dh || !auth) throw new Error("Invalid subscription");

  const { error } = await supabase.rpc("upsert_push_subscription", {
    _endpoint: endpoint,
    _p256dh: p256dh,
    _auth: auth,
    _user_agent: navigator.userAgent,
  });
  if (error) throw error;
}

export async function disablePush(): Promise<void> {
  if (!isPushSupported()) return;
  const reg = await navigator.serviceWorker.getRegistration("/push-sw.js");
  const sub = reg ? await reg.pushManager.getSubscription() : null;
  if (sub) {
    const endpoint = sub.endpoint;
    try {
      await sub.unsubscribe();
    } catch (_) {}
    await supabase.rpc("delete_push_subscription", { _endpoint: endpoint });
  }
}

export async function hasActivePushSubscription(): Promise<boolean> {
  if (!isPushSupported()) return false;
  const reg = await navigator.serviceWorker.getRegistration("/push-sw.js");
  if (!reg) return false;
  const sub = await reg.pushManager.getSubscription();
  return !!sub;
}
